import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useFeatures, useFeatureSwitch } from '@xing-com/hub';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconJobsFilled,
  IconMessages,
  IconMessagesFilled,
  IconNews,
  IconNewsFilled,
  IconNotificationFilled,
} from '@xing-com/icons';
import { Link } from '@xing-com/link';
import { ProductsMenuWithItemsLoggedIn } from '@xing-com/platform-layout-products-menu';

import { DiscountBadge } from './discount-badge';
import {
  StyledProductsMenuWrapper,
  ButtonWithMarkerContainer,
  StyledMarker,
} from './header.styles';
import { MeMenuDropdown } from './me-menu-dropdown';
import { NavItemHeader } from './nav-item-header';
import { NotificationCenterIcon } from './notification-center-icon';
import { SearchDropdown } from './search-dropdown';

const StyledUpsellButton = styled(Button)`
  white-space: nowrap;
`;

export const HeaderNavItemsLoggedIn: React.FC = () => {
  const { formatMessage } = useIntl();

  const { showUpsellHint, showJobSeekerBoneyardUpsellHint } =
    useFeatures()?.data || {};

  const jobsEasyCreateInBoneyardEnabled = useFeatureSwitch(
    'jobs_easy_create_in_boneyard',
    false
  );

  const jobsEasyCreatePath =
    '/recruiting/jobs/create?nwt_nav=boneyard_job_ad&sc_cmp=oow2ac3c767';

  return (
    <>
      <NavItemHeader
        activeItemValues={['jobs']}
        icon={IconJobs}
        activeIcon={IconJobsFilled}
        title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
        href="/jobs/find"
        tracking={{ nwt_nav: 'find_jobs' }}
      />
      <NavItemHeader
        activeItemValues={['my-jobs']}
        icon={IconBookmark}
        activeIcon={IconBookmarkFilled}
        title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
        href="/jobs/my-jobs"
        tracking={{ nwt_nav: 'your_jobs' }}
      />
      <NavItemHeader
        icon={IconContacts}
        activeIcon={IconContactsFilled}
        title={formatMessage({ id: 'MAINNAV_NETWORK' })}
        href="/network"
        activeItemValues={['members']}
        tracking={{ nwt_nav: 'network' }}
      />
      <NavItemHeader
        icon={IconNews}
        activeIcon={IconNewsFilled}
        title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
        href="/discover/updates"
        activeItemValues={['disco']}
        tracking={{ nwt_nav: 'insights' }}
      />
      <NavItemHeader
        icon={IconMessages}
        activeIcon={IconMessagesFilled}
        title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
        href="/chats"
        activeItemValues={['messages']}
        notificationKey="unreadChats"
        tracking={{ nwt_nav: 'messages' }}
      />
      <NavItemHeader
        icon={NotificationCenterIcon}
        activeIcon={IconNotificationFilled}
        provideIconContext
        activeItemValues={['supi']}
        notificationKey="ncWeb"
        href="/notifications"
        title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
        tracking={{ nwt_nav: 'notifications' }}
      />
      <SearchDropdown />
      <MeMenuDropdown />
      {jobsEasyCreateInBoneyardEnabled ? (
        <ButtonWithMarkerContainer>
          <StyledUpsellButton
            variant="primary"
            size="small"
            data-testid="job-easy-create-button"
            href={jobsEasyCreatePath}
          >
            {formatMessage({
              id: 'MAINNAV_JOBS_EASY_CREATE',
            })}
          </StyledUpsellButton>
          <Link to={jobsEasyCreatePath}>
            <StyledMarker variant="strong">
              {formatMessage({ id: 'MAINNAV_JOBS_EASY_CREATE_MARKER' })}
            </StyledMarker>
          </Link>
        </ButtonWithMarkerContainer>
      ) : !showUpsellHint ? null : showJobSeekerBoneyardUpsellHint ? (
        <ButtonWithMarkerContainer>
          <StyledUpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/projobs?reagent=uplt_277"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PROJOBS',
            })}
          </StyledUpsellButton>

          <DiscountBadge product={ProductType.Projobs} />
        </ButtonWithMarkerContainer>
      ) : (
        <ButtonWithMarkerContainer>
          <StyledUpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/premium?reagent=uplt_95"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PREMIUM',
            })}
          </StyledUpsellButton>
          <DiscountBadge product={ProductType.Premium} />
        </ButtonWithMarkerContainer>
      )}
      {
        <StyledProductsMenuWrapper>
          <ProductsMenuWithItemsLoggedIn />
        </StyledProductsMenuWrapper>
      }
    </>
  );
};
